import { Film, Pencil, Trash2, Sparkles } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableRow } from "@/components/ui/table";
import { ActionButton } from "@/components/ui/action-button";
import { ImagePreview } from "@/components/ui/image-preview";
import { StatusBadge } from "@/components/events/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApproveStaged, useRejectStaged } from "@/hooks/useScrapers";
import { toast } from "sonner";
import type { EventStatus, StagedEvent } from "@/types";

function RatingBadge({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-muted text-foreground/80">
      <span className="text-muted-foreground">{label}</span> {value}
    </span>
  );
}

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateStr));
}

interface OckStagedRowProps {
  event: StagedEvent;
  selected: boolean;
  onToggle: () => void;
  onEdit: () => void;
}

export function OckStagedRow({ event, selected, onToggle, onEdit }: OckStagedRowProps) {
  const approve = useApproveStaged();
  const reject = useRejectStaged();
  const meta = event.metadata ?? {};

  const handleStatusChange = async (value: string) => {
    if (event.status !== "pending") {
      toast.error("Wydarzenie zostało już przetworzone");
      return;
    }
    try {
      if (value === "approved") {
        await approve.mutateAsync({
          id: event.id,
          destination: "movies",
          title: event.title,
          eventDescription: event.eventDescription,
          date: event.date,
          registrationType: event.registrationType ?? undefined,
          registrationValue: event.registrationValue ?? undefined,
        });
        toast.success("Film zatwierdzony");
      } else if (value === "rejected") {
        await reject.mutateAsync(event.id);
        toast.success("Film odrzucony");
      }
    } catch (err) {
      toast.error("Nie udało się zmienić statusu", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const isPending = approve.isPending || reject.isPending;

  return (
    <TableRow>
      <TableCell
        className="w-10"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <Checkbox checked={selected} aria-label="Zaznacz wydarzenie" />
      </TableCell>
      <TableCell className="w-[70px] min-w-[70px]">
        {event.imageUrls.length > 0 ? (
          <ImagePreview src={event.imageUrls[0]} alt={event.title} />
        ) : (
          <div
            className="rounded bg-slate-100 flex items-center justify-center"
            style={{ width: 70, height: 70 }}
          >
            <Film className="h-5 w-5 text-slate-400" />
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="font-medium text-sm leading-tight flex items-center gap-1.5">
          {event.title}
          {event.enriched && <Sparkles className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
        </div>
        {meta.originalTitle && meta.originalTitle !== event.title && (
          <div className="text-xs text-muted-foreground italic">{meta.originalTitle}</div>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
        {formatDate(event.date)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{meta.year ?? "—"}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {meta.runtime ? `${meta.runtime} min` : "—"}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{event.room ?? "—"}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{event.language ?? "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1 flex-wrap">
          {meta.filmwebRating && <RatingBadge label="FW" value={meta.filmwebRating} />}
          {meta.imdbRating && <RatingBadge label="IMDb" value={meta.imdbRating} />}
          {meta.rtScore && <RatingBadge label="RT" value={meta.rtScore} />}
        </div>
      </TableCell>
      <TableCell>
        <Select
          value={event.status}
          onValueChange={handleStatusChange}
          disabled={isPending || event.status !== "pending"}
        >
          <SelectTrigger className="w-36 h-8">
            <SelectValue>
              <StatusBadge status={event.status as EventStatus} />
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Oczekujące</SelectItem>
            <SelectItem value="approved">Zatwierdzone</SelectItem>
            <SelectItem value="rejected">Odrzucone</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        {event.status === "pending" && (
          <div className="flex items-center gap-1">
            <ActionButton tooltip="Edytuj" className="h-8 w-8" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
            </ActionButton>
            <ActionButton
              tooltip="Odrzuć"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => handleStatusChange("rejected")}
              disabled={isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </ActionButton>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
